import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Shield, Check, Loader2, Settings, LogOut } from 'lucide-react';

const PLANS = {
  brand_growth: { name: "Brand Growth", price: 99, images: 200 },
  brand_starter: { name: "Brand Starter", price: 199, images: 500 },
  brand_enterprise: { name: "Brand Enterprise", price: 399, images: 1000 },
  influencer_starter: { name: "Influencer Starter", price: 19, images: 50 },
  influencer_pro: { name: "Influencer Pro", price: 79.99, images: 200 },
  personal: { name: "Personal", price: 9, images: 20 }
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated, refreshUser, logout } = useAuth();
  
  const planId = searchParams.get('plan') || user?.plan_id;
  // Read promo from URL params OR localStorage (survives redirects)
  const promoCode = searchParams.get('promo') || localStorage.getItem('vf_promo_code');
  const plan = PLANS[planId];
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [promoInfo, setPromoInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Look up the promo's real duration (1 month vs 6 months) so the summary is accurate
  useEffect(() => {
    if (!promoCode) { setPromoInfo(null); return; }
    fetch(`${API_URL}/api/promo/info?code=${encodeURIComponent(promoCode)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setPromoInfo(d); })
      .catch(() => {});
  }, [promoCode, API_URL]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else if (!planId || !PLANS[planId]) {
      // No valid plan, redirect to subscribe page
      const typeParam = user?.user_type === 'brand' ? 'brand' : 
                       user?.user_type === 'influencer' ? 'influencer' : 'personal';
      navigate('/subscribe?type=' + typeParam);
    }
  }, [isAuthenticated, navigate, planId, user]);

  useEffect(() => {
    if (user?.subscription_status === 'active') {
      localStorage.removeItem('vf_promo_code');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleCheckout = async () => {
    if (loading || checkoutStarted) return; // Prevent double-click
    
    setLoading(true);
    setCheckoutStarted(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          promo_code: promoCode || undefined
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }

      // Clear promo from localStorage before redirecting to Stripe
      localStorage.removeItem('vf_promo_code');
      // Redirect to Stripe
      window.location.href = data.checkout_url;
    } catch (err) {
      // Filter out Service Worker / postMessage cloning errors that are unrelated to checkout
      const msg = err?.message || '';
      const isSwNoise = msg.includes('postMessage') || msg.includes('could not be cloned');
      if (!isSwNoise) {
        setError(msg || 'Checkout failed. Please try again.');
      }
      setLoading(false);
      setCheckoutStarted(false);
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center p-8">
          <p className="text-gray-600 mb-4">No plan selected</p>
          <Link to="/subscribe" className="px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold">
            Choose a Plan
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
            VirtualFit
          </Link>
          {isAuthenticated && (
            <div className="flex items-center gap-2">
              <Link
                to="/settings"
                data-testid="checkout-settings-link"
                className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/70"
                title="Account Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>
              <button
                onClick={logout}
                data-testid="checkout-logout-btn"
                className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/70"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Complete Your Subscription</h1>
          <p className="text-gray-600 mb-8">You're almost there! Just one more step.</p>

          {/* Plan Summary */}
          <div className="bg-gradient-to-r from-rose-50 to-orange-50 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-gray-800 text-lg mb-4">{plan.name}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly subscription</span>
                <span className={`font-semibold ${promoCode ? 'line-through text-gray-400' : ''}`}>${plan.price}</span>
              </div>
              {promoCode && (
                <div className="flex justify-between">
                  <span className="text-emerald-600 font-medium">
                    Promo: {promoInfo && promoInfo.duration_months >= 2
                      ? `First ${promoInfo.duration_months} months free`
                      : 'First month free'}
                  </span>
                  <span className="font-semibold text-emerald-600">-${plan.price}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">AI try-on images</span>
                <span className="font-semibold">{plan.images}/month</span>
              </div>
              <hr className="my-3 border-rose-200" />
              <div className="flex justify-between text-lg">
                <span className="font-bold text-gray-800">Total today</span>
                <span className="font-bold text-rose-500">{promoCode ? '$0.00' : `$${plan.price}`}</span>
              </div>
              {promoCode && (
                <p className="text-xs text-gray-500 mt-1">
                  Then ${plan.price}/month starting{' '}
                  {promoInfo && promoInfo.duration_months >= 2
                    ? `after ${promoInfo.duration_months} months`
                    : 'next billing cycle'}
                </p>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="mb-8">
            <h4 className="font-semibold text-gray-800 mb-3">What's included:</h4>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-600">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                {plan.images} AI try-on images per month
              </li>
              <li className="flex items-center text-gray-600">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                Cancel anytime
              </li>
              <li className="flex items-center text-gray-600">
                <Check className="w-5 h-5 text-green-500 mr-2" />
                Secure payment via Stripe
              </li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Redirecting to payment...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Pay ${plan.price} with Stripe
              </>
            )}
          </button>

          {/* Security Note */}
          <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
            <Shield className="w-4 h-4 mr-2" />
            Secure payment powered by Stripe
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">
            By subscribing, you agree to our <a href="/terms" className="text-rose-400 hover:underline">Terms</a>, <a href="/privacy" className="text-rose-400 hover:underline">Privacy</a>, and <a href="/refund-policy" className="text-rose-400 hover:underline">Refund Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
